import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';
import { CaretDown } from '@phosphor-icons/react';
import { PushPin } from '@phosphor-icons/react/dist/ssr';
import {
  getReferralStatusColor,
  ReferralStatusLabel,
} from '../../shared/referralStatusPresentation';

/* global toastr */

const MAX_VISIBLE_BUCKETS = 6;
const EMPTY_ARRAY = [];
export const HIDDEN_FROM_DEFAULT_STATUS_NAMES = new Set(['Scheduled', 'Mohs Scheduled']);
const HIDDEN_FROM_DEFAULT_TOOLTIP = 'These referrals are hidden from the default inbox view. Use the Status filter to see them in the table.';
const NO_STATUS_DISABLED_TOOLTIP = 'Filtering by "No Status" isn\'t supported yet.';

const getStatusKey = (statusId) => (
  statusId === null || typeof statusId === 'undefined' ? 'null' : String(statusId)
);

const StatusBucketItem = ({
  bucket, color, compact, isActive, disabled, onClick,
}) => {
  const classNames = ['referral-status-count-bar__item'];
  if (compact) classNames.push('referral-status-count-bar__item--compact');
  if (isActive) classNames.push('referral-status-count-bar__item--active');
  if (disabled) classNames.push('referral-status-count-bar__item--disabled');
  if (onClick && !disabled) classNames.push('referral-status-count-bar__item--clickable');

  const innerContent = (
    <>
      <span
        className="referral-status-count-bar__dot"
        style={{ backgroundColor: color }}
        aria-hidden="true"
      />
      <span className="referral-status-count-bar__item-name">{bucket.name}</span>
      <span className="referral-status-count-bar__item-count">{bucket.count}</span>
    </>
  );

  let itemContent;
  if (onClick && !disabled) {
    itemContent = (
      <button
        type="button"
        className={classNames.join(' ')}
        aria-pressed={isActive}
        onClick={() => onClick(bucket)}
      >
        {innerContent}
      </button>
    );
  } else {
    itemContent = (
      <div
        className={classNames.join(' ')}
        aria-disabled={disabled || undefined}
      >
        {innerContent}
      </div>
    );
  }

  if (disabled) {
    return <Tooltip title={NO_STATUS_DISABLED_TOOLTIP}>{itemContent}</Tooltip>;
  }

  if (HIDDEN_FROM_DEFAULT_STATUS_NAMES.has(bucket.name)) {
    return <Tooltip title={HIDDEN_FROM_DEFAULT_TOOLTIP}>{itemContent}</Tooltip>;
  }

  return itemContent;
};

StatusBucketItem.defaultProps = {
  compact: false,
  isActive: false,
  disabled: false,
  onClick: null,
};

StatusBucketItem.propTypes = {
  bucket: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
  }).isRequired,
  color: PropTypes.string.isRequired,
  compact: PropTypes.bool,
  isActive: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
};

const buildOrderedBuckets = (statusCounts, allReferredStatuses, showUnpinnedStatuses) => {
  const counts = Array.isArray(statusCounts) ? statusCounts : EMPTY_ARRAY;
  const statuses = Array.isArray(allReferredStatuses) ? allReferredStatuses : EMPTY_ARRAY;
  const visibleStatuses = showUnpinnedStatuses === false
    ? statuses.filter((status) => status.bar_pinned === true)
    : statuses;

  if (visibleStatuses.length === 0) {
    return counts.filter((bucket) => bucket.id === null);
  }

  const countsByKey = counts.reduce((memo, bucket) => {
    memo.set(getStatusKey(bucket.id), bucket);
    return memo;
  }, new Map());

  const orderedBuckets = visibleStatuses.map((status) => (
    countsByKey.get(getStatusKey(status.id)) || {
      id: status.id,
      name: status.name,
      count: 0,
    }
  ));

  const allStatusKeys = new Set(statuses.map((status) => getStatusKey(status.id)));
  const knownKeys = new Set(visibleStatuses.map((status) => getStatusKey(status.id)));
  const extraBuckets = [];
  let noStatusBucket = null;

  counts.forEach((bucket) => {
    const bucketKey = getStatusKey(bucket.id);
    if (knownKeys.has(bucketKey)) return;
    if (allStatusKeys.has(bucketKey)) return;

    if (bucket.id === null) {
      noStatusBucket = bucket;
      return;
    }

    extraBuckets.push(bucket);
  });

  return [
    ...orderedBuckets,
    ...extraBuckets,
    ...(noStatusBucket ? [noStatusBucket] : []),
  ];
};

const buildPreferencesPayload = (statuses) => ({
  ordered_status_ids: statuses.map((status) => status.id),
  pinned_status_ids: statuses
    .filter((status) => status.bar_pinned === true)
    .map((status) => status.id),
});

const replaceSectionStatuses = (statuses, reorderedStatuses, isPinnedSection) => {
  let sectionIndex = 0;

  return statuses.map((status) => {
    if ((status.bar_pinned === true) !== isPinnedSection) return status;

    const nextStatus = reorderedStatuses[sectionIndex];
    sectionIndex += 1;
    return nextStatus;
  });
};

const moveStatusWithinSection = (statuses, statusId, direction) => {
  const targetStatus = statuses.find((status) => status.id === statusId);
  if (!targetStatus) return null;

  const isPinnedSection = targetStatus.bar_pinned === true;
  const sectionStatuses = statuses.filter((status) => (status.bar_pinned === true) === isPinnedSection);
  const currentSectionIndex = sectionStatuses.findIndex((status) => status.id === statusId);
  const nextSectionIndex = currentSectionIndex + direction;

  if (currentSectionIndex < 0 || nextSectionIndex < 0 || nextSectionIndex >= sectionStatuses.length) {
    return null;
  }

  const reorderedSectionStatuses = [...sectionStatuses];
  const [movedStatus] = reorderedSectionStatuses.splice(currentSectionIndex, 1);
  reorderedSectionStatuses.splice(nextSectionIndex, 0, movedStatus);

  return replaceSectionStatuses(statuses, reorderedSectionStatuses, isPinnedSection);
};

const ReferralStatusCountBar = ({
  statusCounts,
  allReferredStatuses,
  showUnpinnedStatuses,
  total,
  updatedAt,
  loading,
  hasError,
  savingPreferences,
  updateReferralStatusBarPreferences,
  selectedStatusNames,
  onBucketClick,
}) => {
  const isBucketActive = (bucket) => {
    if (bucket.id === null || typeof bucket.id === 'undefined') return false;
    const normalizedName = String(bucket.name || '').toLowerCase();
    return selectedStatusNames.some((value) => String(value).toLowerCase() === normalizedName);
  };

  const handleBucketClick = onBucketClick || null;
  const overflowRef = useRef(null);
  const customizeRef = useRef(null);
  const preferencesSaveInFlightRef = useRef(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);

  const orderedBuckets = useMemo(
    () => buildOrderedBuckets(statusCounts, allReferredStatuses, showUnpinnedStatuses),
    [allReferredStatuses, showUnpinnedStatuses, statusCounts],
  );

  const visibleBuckets = useMemo(
    () => orderedBuckets.slice(0, MAX_VISIBLE_BUCKETS),
    [orderedBuckets],
  );
  const overflowBuckets = useMemo(
    () => orderedBuckets.slice(MAX_VISIBLE_BUCKETS),
    [orderedBuckets],
  );
  const customizableStatuses = useMemo(
    () => (Array.isArray(allReferredStatuses) ? allReferredStatuses : EMPTY_ARRAY),
    [allReferredStatuses],
  );
  const baseOrderedStatuses = useMemo(
    () => [...customizableStatuses].sort((leftStatus, rightStatus) => (
      (leftStatus.bar_order_position ?? Number.MAX_SAFE_INTEGER)
      - (rightStatus.bar_order_position ?? Number.MAX_SAFE_INTEGER)
    )),
    [customizableStatuses],
  );
  const pinnedStatuses = useMemo(
    () => baseOrderedStatuses.filter((status) => status.bar_pinned === true),
    [baseOrderedStatuses],
  );
  const unpinnedStatuses = useMemo(
    () => baseOrderedStatuses.filter((status) => status.bar_pinned !== true),
    [baseOrderedStatuses],
  );

  useEffect(() => {
    if (!customizeOpen) return undefined;

    const handlePointerDown = (event) => {
      if (customizeRef.current?.contains(event.target)) return;
      setCustomizeOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setCustomizeOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [customizeOpen]);

  useEffect(() => {
    if (!overflowOpen) return undefined;

    const handlePointerDown = (event) => {
      if (overflowRef.current?.contains(event.target)) return;
      setOverflowOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setOverflowOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [overflowOpen]);

  const handleOverflowBucketClick = (bucket) => {
    if (handleBucketClick) handleBucketClick(bucket);
    setOverflowOpen(false);
  };

  const persistPreferences = async (nextStatuses, nextShowUnpinnedStatuses = showUnpinnedStatuses) => {
    if (preferencesSaveInFlightRef.current) return;

    preferencesSaveInFlightRef.current = true;

    try {
      const response = await updateReferralStatusBarPreferences({
        ...buildPreferencesPayload(nextStatuses),
        show_unpinned_statuses: nextShowUnpinnedStatuses,
      });

      if (response?.error && typeof toastr !== 'undefined') {
        toastr.error(response.response?.data?.error || 'Unable to update the status bucket order.');
      }
    } finally {
      preferencesSaveInFlightRef.current = false;
    }
  };

  const handleMoveStatus = (statusId, direction) => {
    if (savingPreferences || preferencesSaveInFlightRef.current) return;

    const nextStatuses = moveStatusWithinSection(baseOrderedStatuses, statusId, direction);
    if (!nextStatuses) return;

    persistPreferences(nextStatuses);
  };

  const handleTogglePin = (statusId) => {
    if (savingPreferences || preferencesSaveInFlightRef.current) return;

    const nextStatuses = baseOrderedStatuses.map((status) => (
      status.id === statusId
        ? { ...status, bar_pinned: status.bar_pinned !== true }
        : status
    ));

    persistPreferences(nextStatuses);
  };

  const handleToggleShowUnpinnedStatuses = () => {
    if (savingPreferences || preferencesSaveInFlightRef.current) return;

    persistPreferences(baseOrderedStatuses, !showUnpinnedStatuses);
  };

  return (
    <section
      className={`referral-status-count-bar${loading ? ' referral-status-count-bar--loading' : ''}`}
      data-testid="referral-status-count-bar"
      data-updated-at={updatedAt || ''}
    >
      <div className="referral-status-count-bar__row">
        <div className="referral-status-count-bar__label">
          <span className="referral-status-count-bar__label-icon" aria-hidden="true" />
          <span>STATUS BUCKETS</span>
        </div>

        <div className="referral-status-count-bar__separator" aria-hidden="true" />

        <div className="referral-status-count-bar__all" data-testid="referral-status-count-bar-all">
          <span className="referral-status-count-bar__all-label">All</span>
          <span className="referral-status-count-bar__all-count">{total}</span>
        </div>

        {hasError && (
          <div className="referral-status-count-bar__error" role="status">
            Showing previous counts
          </div>
        )}

        <div className="referral-status-count-bar__separator" aria-hidden="true" />

        <div className="referral-status-count-bar__items">
          {visibleBuckets.map((bucket, index) => (
            <StatusBucketItem
              key={bucket.id || bucket.name}
              bucket={bucket}
              color={getReferralStatusColor(bucket, orderedBuckets, index)}
              isActive={isBucketActive(bucket)}
              disabled={bucket.id === null || typeof bucket.id === 'undefined'}
              onClick={handleBucketClick}
            />
          ))}

          {overflowBuckets.length > 0 && (
            <div
              className="referral-status-count-bar__overflow"
              ref={overflowRef}
            >
              <button
                type="button"
                className="referral-status-count-bar__overflow-trigger"
                aria-expanded={overflowOpen}
                aria-haspopup="menu"
                onClick={() => setOverflowOpen((currentValue) => !currentValue)}
              >
                <span>+{overflowBuckets.length}</span>
                <CaretDown size={12} />
              </button>

              {overflowOpen && (
                <div className="referral-status-count-bar__overflow-menu" role="menu">
                  {overflowBuckets.map((bucket, index) => (
                    <StatusBucketItem
                      key={bucket.id || bucket.name}
                      bucket={bucket}
                      color={getReferralStatusColor(bucket, orderedBuckets, visibleBuckets.length + index)}
                      compact
                      isActive={isBucketActive(bucket)}
                      disabled={bucket.id === null || typeof bucket.id === 'undefined'}
                      onClick={handleBucketClick ? handleOverflowBucketClick : null}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {customizableStatuses.length > 0 && (
          <>
            <div className="referral-status-count-bar__separator" aria-hidden="true" />
            <div className="referral-status-count-bar__customize" ref={customizeRef}>
              <button
                type="button"
                className="referral-status-count-bar__customize-trigger"
                onClick={() => setCustomizeOpen((currentValue) => !currentValue)}
                aria-expanded={customizeOpen}
                aria-haspopup="dialog"
                disabled={savingPreferences}
              >
                {savingPreferences ? 'Saving...' : 'Customize'}
              </button>

              {customizeOpen && (
                <div className="referral-status-count-bar__customize-menu" role="dialog" aria-label="Customize status buckets">
                  <div className="referral-status-count-bar__customize-header">
                    <div className="referral-status-count-bar__customize-title">Pinned statuses stay first.</div>
                    <button
                      type="button"
                      className="referral-status-count-bar__customize-close"
                      onClick={() => setCustomizeOpen(false)}
                      aria-label="Close customize status buckets"
                    >
                      Close
                    </button>
                  </div>
                  <button
                    type="button"
                    className="referral-status-count-bar__customize-toggle"
                    onClick={handleToggleShowUnpinnedStatuses}
                    disabled={savingPreferences}
                  >
                    {showUnpinnedStatuses ? "Don't Show Unpinned" : 'Show Unpinned'}
                  </button>

                  {pinnedStatuses.length > 0 && (
                    <div className="referral-status-count-bar__customize-section">
                      <div className="referral-status-count-bar__customize-section-label">Pinned</div>
                      {pinnedStatuses.map((status, statusIndex) => (
                        <div className="referral-status-count-bar__customize-row" key={status.id}>
                          <ReferralStatusLabel
                            status={status}
                            statuses={customizableStatuses}
                            fallbackIndex={statusIndex}
                            inactive={status.active === false}
                          />
                          <div className="referral-status-count-bar__customize-actions">
                            <button
                              type="button"
                              className="referral-status-count-bar__customize-action referral-status-count-bar__customize-action--icon"
                              onClick={() => handleTogglePin(status.id)}
                              disabled={savingPreferences}
                              aria-label={`Unpin ${status.name}`}
                              title="Unpin"
                            >
                              <PushPin size={13} weight="fill" />
                            </button>
                            <button
                              type="button"
                              className="referral-status-count-bar__customize-action"
                              onClick={() => handleMoveStatus(status.id, -1)}
                              disabled={savingPreferences || statusIndex === 0}
                            >
                              Up
                            </button>
                            <button
                              type="button"
                              className="referral-status-count-bar__customize-action"
                              onClick={() => handleMoveStatus(status.id, 1)}
                              disabled={savingPreferences || statusIndex === pinnedStatuses.length - 1}
                            >
                              Down
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {pinnedStatuses.length > 0 && unpinnedStatuses.length > 0 && (
                    <div className="referral-status-count-bar__customize-divider" aria-hidden="true" />
                  )}

                  {unpinnedStatuses.length > 0 && (
                    <div className="referral-status-count-bar__customize-section">
                      <div className="referral-status-count-bar__customize-section-label">Unpinned</div>
                      {unpinnedStatuses.map((status, statusIndex) => (
                        <div className="referral-status-count-bar__customize-row" key={status.id}>
                          <ReferralStatusLabel
                            status={status}
                            statuses={customizableStatuses}
                            fallbackIndex={statusIndex}
                            inactive={status.active === false}
                          />
                          <div className="referral-status-count-bar__customize-actions">
                            <button
                              type="button"
                              className="referral-status-count-bar__customize-action referral-status-count-bar__customize-action--icon referral-status-count-bar__customize-action--icon-muted"
                              onClick={() => handleTogglePin(status.id)}
                              disabled={savingPreferences}
                              aria-label={`Pin ${status.name}`}
                              title="Pin"
                            >
                              <PushPin size={13} />
                            </button>
                            <button
                              type="button"
                              className="referral-status-count-bar__customize-action"
                              onClick={() => handleMoveStatus(status.id, -1)}
                              disabled={savingPreferences || statusIndex === 0}
                            >
                              Up
                            </button>
                            <button
                              type="button"
                              className="referral-status-count-bar__customize-action"
                              onClick={() => handleMoveStatus(status.id, 1)}
                              disabled={savingPreferences || statusIndex === unpinnedStatuses.length - 1}
                            >
                              Down
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

ReferralStatusCountBar.defaultProps = {
  updatedAt: null,
  allReferredStatuses: [],
  showUnpinnedStatuses: true,
  hasError: false,
  savingPreferences: false,
  selectedStatusNames: [],
  onBucketClick: null,
};

ReferralStatusCountBar.propTypes = {
  statusCounts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
  })).isRequired,
  allReferredStatuses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    active: PropTypes.bool,
    bar_pinned: PropTypes.bool,
    bar_order_position: PropTypes.number,
  })),
  showUnpinnedStatuses: PropTypes.bool,
  total: PropTypes.number.isRequired,
  updatedAt: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  hasError: PropTypes.bool,
  savingPreferences: PropTypes.bool,
  updateReferralStatusBarPreferences: PropTypes.func.isRequired,
  selectedStatusNames: PropTypes.arrayOf(PropTypes.string),
  onBucketClick: PropTypes.func,
};

export default ReferralStatusCountBar;
